/**
 * KategoriAdmin - Administrer beviskategorier
 * Opprett, rediger og slett kategorier for organisering av bevis
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './KategoriAdmin.css';

export default function KategoriAdmin({ sakId, onClose, onKategorierOppdatert }) {
  const [kategorier, setKategorier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redigerKategori, setRedigerKategori] = useState(null);
  const [visNyKategori, setVisNyKategori] = useState(false);

  const [nyKategori, setNyKategori] = useState({
    navn: '',
    beskrivelse: '',
    farge: '#3b82f6',
    ikon: 'folder'
  });

  // Forhåndsdefinerte farger for enkel valg
  const forhåndsvalgteFarger = [
    { navn: 'Rød', hex: '#ef4444' },
    { navn: 'Oransje', hex: '#f97316' },
    { navn: 'Gul', hex: '#eab308' },
    { navn: 'Grønn', hex: '#22c55e' },
    { navn: 'Blå', hex: '#3b82f6' },
    { navn: 'Lilla', hex: '#a855f7' },
    { navn: 'Rosa', hex: '#ec4899' },
    { navn: 'Grå', hex: '#6b7280' }
  ];

  // Standard kategorier som kan opprettes raskt
  const standardKategorier = [
    { navn: 'Mangelsbevis', beskrivelse: 'Dokumentasjon av mangler ved eiendom', farge: '#ef4444', ikon: 'folder' },
    { navn: 'Økonomiske dokumenter', beskrivelse: 'Fakturaer, kvitteringer, budsjett', farge: '#22c55e', ikon: 'folder' },
    { navn: 'Korrespondanse', beskrivelse: 'E-post, brev, notater', farge: '#3b82f6', ikon: 'folder' },
    { navn: 'Tekniske rapporter', beskrivelse: 'Takstrapporter, ingeniørrapporter', farge: '#f97316', ikon: 'folder' },
    { navn: 'Kontrakter og avtaler', beskrivelse: 'Signerte avtaler og kontrakter', farge: '#a855f7', ikon: 'folder' },
    { navn: 'Vitneforklaringer', beskrivelse: 'Vitneerklæringer og avhør', farge: '#eab308', ikon: 'folder' }
  ];

  useEffect(() => {
    if (sakId) {
      lastKategorier();
    }
  }, [sakId]);

  const lastKategorier = async () => {
    try {
      setLoading(true);
      const response = await crmApi.request(`/bevis/kategorier?sak_id=${sakId}`);
      const data = await response.json();
      setKategorier(data.kategorier || []);
    } catch (error) {
      console.error('Feil ved lasting av kategorier:', error);
    } finally {
      setLoading(false);
    }
  };

  const opprettKategori = async (kategoriData) => {
    try {
      const response = await crmApi.request('/bevis/kategorier', {
        method: 'POST',
        body: JSON.stringify({
          sak_id: sakId,
          ...kategoriData
        })
      });

      if (response.ok) {
        await lastKategorier();
        onKategorierOppdatert?.();
        setNyKategori({ navn: '', beskrivelse: '', farge: '#3b82f6', ikon: 'folder' });
        setVisNyKategori(false);
        return true;
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke opprette kategori'}`);
        return false;
      }
    } catch (error) {
      console.error('Feil ved oppretting av kategori:', error);
      alert('Kunne ikke opprette kategori');
      return false;
    }
  };

  const oppdaterKategori = async (kategoriId, data) => {
    try {
      const response = await crmApi.request(`/bevis/kategorier/${kategoriId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });

      if (response.ok) {
        await lastKategorier();
        onKategorierOppdatert?.();
        setRedigerKategori(null);
        return true;
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke oppdatere kategori'}`);
        return false;
      }
    } catch (error) {
      console.error('Feil ved oppdatering av kategori:', error);
      alert('Kunne ikke oppdatere kategori');
      return false;
    }
  };

  const slettKategori = async (kategoriId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne kategorien?\n\nBevis tilknyttet kategorien vil ikke bli slettet, men kategorikoblingen vil fjernes.')) {
      return;
    }

    try {
      const response = await crmApi.request(`/bevis/kategorier/${kategoriId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await lastKategorier();
        onKategorierOppdatert?.();
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke slette kategori'}`);
      }
    } catch (error) {
      console.error('Feil ved sletting av kategori:', error);
      alert('Kunne ikke slette kategori');
    }
  };

  const opprettStandardKategori = async (standardKat) => {
    await opprettKategori(standardKat);
  };

  const lagreNyKategori = async (e) => {
    e.preventDefault();

    if (!nyKategori.navn.trim()) {
      alert('Kategorinavn er påkrevd');
      return;
    }

    await opprettKategori(nyKategori);
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content kategori-admin-modal" onClick={(e) => e.stopPropagation()}>
          <div className="kategori-loading">📁 Laster kategorier...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content kategori-admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kategori-admin-header">
          <div>
            <h2>📁 Administrer kategorier</h2>
            <p>Organiser bevis med kategorier for enklere oversikt</p>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="kategori-admin-body">
          {/* Eksisterende kategorier */}
          {kategorier.length > 0 && (
            <div className="kategori-section">
              <h3>Dine kategorier ({kategorier.length})</h3>

              <div className="kategorier-liste">
                {kategorier.map((kat) => (
                  <div key={kat.id} className="kategori-item">
                    <div className="kategori-item-info">
                      <div
                        className="kategori-farge-display"
                        style={{ backgroundColor: kat.farge }}
                      />
                      <div className="kategori-details">
                        <h4>{kat.navn}</h4>
                        {kat.beskrivelse && <p>{kat.beskrivelse}</p>}
                      </div>
                    </div>

                    <div className="kategori-item-actions">
                      <button
                        className="btn-icon"
                        onClick={() => setRedigerKategori(kat)}
                        title="Rediger"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => slettKategori(kat.id)}
                        title="Slett"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ny kategori */}
          {!visNyKategori && (
            <button
              className="btn-primary btn-full"
              onClick={() => setVisNyKategori(true)}
            >
              ➕ Opprett ny kategori
            </button>
          )}

          {visNyKategori && (
            <div className="kategori-section">
              <h3>Opprett ny kategori</h3>

              <form onSubmit={lagreNyKategori}>
                <div className="form-group">
                  <label>Kategorinavn *</label>
                  <input
                    type="text"
                    value={nyKategori.navn}
                    onChange={(e) => setNyKategori({...nyKategori, navn: e.target.value})}
                    placeholder="F.eks. 'Mangelsbevis'"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Beskrivelse</label>
                  <textarea
                    value={nyKategori.beskrivelse}
                    onChange={(e) => setNyKategori({...nyKategori, beskrivelse: e.target.value})}
                    placeholder="Hva slags bevis hører hjemme i denne kategorien?"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Farge</label>
                  <div className="farge-velger">
                    {forhåndsvalgteFarger.map((farge) => (
                      <button
                        key={farge.hex}
                        type="button"
                        className={`farge-knapp ${nyKategori.farge === farge.hex ? 'active' : ''}`}
                        style={{ backgroundColor: farge.hex }}
                        onClick={() => setNyKategori({...nyKategori, farge: farge.hex})}
                        title={farge.navn}
                      />
                    ))}
                    <input
                      type="color"
                      value={nyKategori.farge}
                      onChange={(e) => setNyKategori({...nyKategori, farge: e.target.value})}
                      className="farge-velger-input"
                      title="Velg egen farge"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setVisNyKategori(false);
                      setNyKategori({ navn: '', beskrivelse: '', farge: '#3b82f6', ikon: 'folder' });
                    }}
                  >
                    Avbryt
                  </button>
                  <button type="submit" className="btn-primary">
                    Opprett kategori
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Standard kategorier (kun vis hvis ingen kategorier finnes) */}
          {kategorier.length === 0 && !visNyKategori && (
            <div className="kategori-section">
              <h3>Hurtigstart: Standard kategorier</h3>
              <p className="hint">Klikk for å opprette en standard kategori raskt</p>

              <div className="standard-kategorier-grid">
                {standardKategorier.map((stdKat, index) => (
                  <button
                    key={index}
                    className="standard-kategori-knapp"
                    onClick={() => opprettStandardKategori(stdKat)}
                  >
                    <div
                      className="standard-kategori-farge"
                      style={{ backgroundColor: stdKat.farge }}
                    />
                    <div className="standard-kategori-info">
                      <h4>{stdKat.navn}</h4>
                      <p>{stdKat.beskrivelse}</p>
                    </div>
                    <span className="plus-icon">+</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="kategori-admin-footer">
          <button className="btn-secondary" onClick={onClose}>
            Lukk
          </button>
        </div>

        {/* Rediger kategori modal */}
        {redigerKategori && (
          <div className="modal-overlay" onClick={() => setRedigerKategori(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>✏️ Rediger kategori</h2>

              <form onSubmit={(e) => {
                e.preventDefault();
                oppdaterKategori(redigerKategori.id, {
                  navn: redigerKategori.navn,
                  beskrivelse: redigerKategori.beskrivelse,
                  farge: redigerKategori.farge
                });
              }}>
                <div className="form-group">
                  <label>Kategorinavn *</label>
                  <input
                    type="text"
                    value={redigerKategori.navn}
                    onChange={(e) => setRedigerKategori({...redigerKategori, navn: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Beskrivelse</label>
                  <textarea
                    value={redigerKategori.beskrivelse || ''}
                    onChange={(e) => setRedigerKategori({...redigerKategori, beskrivelse: e.target.value})}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Farge</label>
                  <div className="farge-velger">
                    {forhåndsvalgteFarger.map((farge) => (
                      <button
                        key={farge.hex}
                        type="button"
                        className={`farge-knapp ${redigerKategori.farge === farge.hex ? 'active' : ''}`}
                        style={{ backgroundColor: farge.hex }}
                        onClick={() => setRedigerKategori({...redigerKategori, farge: farge.hex})}
                        title={farge.navn}
                      />
                    ))}
                    <input
                      type="color"
                      value={redigerKategori.farge}
                      onChange={(e) => setRedigerKategori({...redigerKategori, farge: e.target.value})}
                      className="farge-velger-input"
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setRedigerKategori(null)}
                  >
                    Avbryt
                  </button>
                  <button type="submit" className="btn-primary">
                    Lagre endringer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
